import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Image,
  Divider,
  Badge,
  Card,
  CardBody,
  SimpleGrid,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  ViewIcon, 
  CalendarIcon, 
  ChatIcon,
  StarIcon,
  LockIcon,
  EditIcon,
  CheckCircleIcon,
  DownloadIcon,
  InfoIcon,
  LinkIcon,
  AddIcon
} from '@chakra-ui/icons';

export default function UserGuide() {
  const { isFunctionEnabled } = useAuth();

  // 基本フロー
  const basicFlow = [
    {
      step: 1,
      title: "選び方ガイドを確認",
      description: "設備の選び方や特徴を学びます",
      icon: SearchIcon,
      color: "gray.600",
      imagePlaceholder: "guide_overview.png"
    },
    {
      step: 2,
      title: "設備を選択",
      description: "お好みの設備を選択・仮確定します",
      icon: ViewIcon,
      color: "gray.600",
      imagePlaceholder: "equipment_selection.png"
    },
    {
      step: 3,
      title: "概算見積もり",
      description: "選択したものの概算見積もりを確認します",
      icon: InfoIcon,
      color: "gray.600",
      imagePlaceholder: "estimate_summary.png"
    }
  ];

  // オプション機能
  const optionalFeatures = [
    {
      title: "ご相談チャット",
      description: "分からないことがあればいつでもチャットで相談できます。設備固有のご相談は設備選択ページから、その他のご質問は上部「ご相談」ページよりお問い合わせください。",
      icon: ChatIcon,
      isEnabled: isFunctionEnabled('chat'),
      imagePlaceholder: "chat_feature.png"
    },
    {
      title: "来店予約",
      description: "簡単な情報入力で来店予約ができます。ちょっとしたご相談でも構いませんので、是非ご来店ください。",
      icon: CalendarIcon,
      isEnabled: true, // 来店予約は常に有効
      imagePlaceholder: "visit_reservation.png"
    },
    {
      title: "お気に入り画像",
      description: "どんな住まいが理想かイメージ画像を共有して好みをお伝えできます",
      icon: StarIcon,
      isEnabled: true, // お気に入り画像は常に有効
      imagePlaceholder: "like_images.png"
    },
    {
      title: "間取り編集",
      description: "自由に間取りを作成・編集できます",
      icon: EditIcon,
      isEnabled: isFunctionEnabled('floor_plan'),
      imagePlaceholder: "floor_planner.png"
    },
    {
      title: "LINE連携",
      description: "LINEと連携することで、当社からのチャット連絡の新着通知が入ります。",
      icon: LinkIcon,
      isEnabled: true, // 全テナントで有効
      imagePlaceholder: "line_integration.png"
    },
    {
      title: "家族招待",
      description: "ご家族を招待して一緒に設備選択や情報共有ができます",
      icon: AddIcon,
      isEnabled: true, // 全テナントで有効
      imagePlaceholder: "family_invitation.png"
    }
  ];

  // 確定・ロック機能
  const lockFeatures = [
    {
      title: "設備ごとのロック",
      description: "当社と一緒に設備を確定します",
      icon: LockIcon,
      isEnabled: isFunctionEnabled('lock'),
      imagePlaceholder: "equipment_lock.png"
    },
    {
      title: "全確定＆Excel出力",
      description: "すべての設備が確定したら概算見積もりページでExcel出力ができます",
      icon: DownloadIcon,
      isEnabled: true, // 全確定は全テナントで有効
      imagePlaceholder: "excel_export.png"
    }
  ];

  return (
    <Container maxW="6xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* ヘッダー */}
        <Box textAlign="center">
          <Heading size="xl" mb={4} color="gray.700">
            使い方ガイド
          </Heading>
          <Text fontSize="lg" color="gray.600">
           使い方について解説します
          </Text>
        </Box>

        {/* 基本フロー */}
        <Box>
          <Heading size="lg" mb={6} color="gray.700">
            基本的な流れ
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {basicFlow.map((item) => (
              <Card key={item.step} variant="outline" borderColor="gray.200">
                <CardBody>
                  <VStack spacing={4} align="center" textAlign="center">
                    <HStack>
                      <Badge colorScheme="gray" fontSize="lg" px={3} py={1}>
                        {item.step}
                      </Badge>
                      <Icon as={item.icon} w={6} h={6} color={item.color} />
                    </HStack>
                    
                    <Heading size="md" color="gray.700">
                      {item.title}
                    </Heading>
                    
                    {/* 画像表示 */}
                    <Box
                      w="100%"
                      h={{ base: "300px", md: "250px" }}
                      bg="gray.100"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px solid"
                      borderColor="gray.300"
                    >
                      <Image
                        src={`/images/guide/${item.imagePlaceholder}`}
                        alt={item.title}
                        w="100%"
                        h="100%"
                        objectFit="contain"
                        fallback={
                          <Box
                            w="100%"
                            h="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            border="2px dashed"
                            borderColor="gray.300"
                          >
                            <Text color="gray.500" fontSize="sm" textAlign="center">
                              {item.imagePlaceholder}
                              <br />
                              （画像準備中）
                            </Text>
                          </Box>
                        }
                      />
                    </Box>
                    
                    <Text color="gray.600">
                      {item.description}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        <Divider />

        {/* オプション機能 */}
        <Box>
          <Heading size="lg" mb={6} color="gray.700">
            便利な機能
          </Heading>
          <Text mb={6} color="gray.600">
            以下機能もありますので、是非ご活用ください。
          </Text>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {optionalFeatures.filter(feature => feature.isEnabled).map((feature, index) => (
              <Card key={index} variant="outline" borderColor="gray.200">
                <CardBody>
                  <VStack spacing={4} align="start">
                    <HStack>
                      <Icon as={feature.icon} w={5} h={5} color="yellow.600" />
                      <Heading size="md" color="gray.700">
                        {feature.title}
                      </Heading>
                    </HStack>
                    
                    {/* 画像表示 */}
                    <Box
                      w="100%"
                      h={{ base: "250px", md: "200px" }}
                      bg="gray.100"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px solid"
                      borderColor="gray.300"
                    >
                      <Image
                        src={`/images/guide/${feature.imagePlaceholder}`}
                        alt={feature.title}
                        w="100%"
                        h="100%"
                        objectFit="contain"
                        fallback={
                          <Box
                            w="100%"
                            h="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            border="2px dashed"
                            borderColor="gray.300"
                          >
                            <Text color="gray.500" fontSize="sm" textAlign="center">
                              {feature.imagePlaceholder}
                              <br />
                              （画像準備中）
                            </Text>
                          </Box>
                        }
                      />
                    </Box>
                    
                    <Text color="gray.600">
                      {feature.description}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        {/* 確定・ロック機能（有効な場合のみ表示） */}
        {lockFeatures.some(feature => feature.isEnabled) && (
          <>
            <Divider />
            <Box>
              <Heading size="lg" mb={6} color="gray.700">
                確定・ロック機能
              </Heading>
              <Text mb={6} color="gray.600">
                これらの機能は当社の担当者と一緒に行います
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {lockFeatures.filter(feature => feature.isEnabled).map((feature, index) => (
                  <Card key={index} variant="outline" borderColor="gray.200">
                    <CardBody>
                      <VStack spacing={4} align="start">
                        <HStack>
                          <Icon as={feature.icon} w={5} h={5} color="yellow.600" />
                          <Heading size="md" color="gray.700">
                            {feature.title}
                          </Heading>
                        </HStack>
                        
                        {/* 画像表示 */}
                        <Box
                          w="100%"
                          h={{ base: "250px", md: "200px" }}
                          bg="gray.100"
                          borderRadius="md"
                          overflow="hidden"
                          border="1px solid"
                          borderColor="gray.300"
                        >
                          <Image
                            src={`/images/guide/${feature.imagePlaceholder}`}
                            alt={feature.title}
                            w="100%"
                            h="100%"
                            objectFit="contain"
                            fallback={
                              <Box
                                w="100%"
                                h="100%"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                border="2px dashed"
                                borderColor="gray.300"
                              >
                                <Text color="gray.500" fontSize="sm" textAlign="center">
                                  {feature.imagePlaceholder}
                                  <br />
                                  （画像準備中）
                                </Text>
                              </Box>
                            }
                          />
                        </Box>
                        
                        <Text color="gray.600">
                          {feature.description}
                        </Text>
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>
            </Box>
          </>
        )}

      </VStack>
    </Container>
  );
}
